import { useEffect, useState } from "react";
import { Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const EMPTY = { name: "", qty: 1, price: "" };

export function CreateShipmentDialog({ open, onOpenChange, ktList, onDone, preset, products = [] }) {
  const [kt, setKt] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([{ ...EMPTY }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !preset) return;
    setKt(preset.kt_user_id || "");
    setNote(preset.note || "");
    setItems(preset.items?.length ? preset.items.map((i) => ({ name: i.name, qty: i.qty || 1, price: products.find((p) => p.name.toLowerCase() === i.name.toLowerCase())?.price || "" })) : [{ ...EMPTY }]);
  }, [open, preset]); // eslint-disable-line
  const set = (i, patch) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch, ...(patch.name !== undefined && !it.price ? { price: products.find((p) => p.name.toLowerCase() === patch.name.toLowerCase())?.price || "" } : {}) } : it)));
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const submit = async (e) => {
    e.preventDefault();
    if (!kt) return toast.error("Pilih teknisi kepala penerima");
    setBusy(true);
    try {
      const { data } = await api.post("/asset-shipments", { kt_user_id: kt, note, request_id: preset?.request_id || "", items: items.map((it) => ({ name: it.name, qty: Number(it.qty) || 1, price: Number(it.price) || 0 })) });
      toast.success(`Kiriman ${data.code} disiapkan`);
      setKt(""); setNote(""); setItems([{ ...EMPTY }]);
      onOpenChange(false); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-create-shipment">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Send className="w-5 h-5 text-sky-500" /> Siapkan Kiriman Aset</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <datalist id="ship-product-names">{products.map((p) => <option key={p.id} value={p.name} />)}</datalist>
          <div className="space-y-1.5"><Label>Penerima (Teknisi Kepala)</Label>
            <Select value={kt} onValueChange={setKt}>
              <SelectTrigger data-testid="select-shipment-kt"><SelectValue placeholder="Pilih akun teknisi kepala..." /></SelectTrigger>
              <SelectContent>{ktList.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`shipment-kt-opt-${u.id}`}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
            </Select>
            {!ktList.length && <p className="text-[11px] text-amber-500">Belum ada akun dengan role Teknisi Kepala. Atur role di menu Agen & Komisi.</p>}
          </div>
          <div className="space-y-2">
            <Label>Daftar Barang / Alat</Label>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_64px_110px_32px] gap-1.5 items-center" data-testid={`shipment-item-${i}`}>
                <Input required list="ship-product-names" value={it.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Nama barang (cth: ONT, Kabel FO 100m)" className="h-9 text-sm" data-testid={`shipment-item-name-${i}`} />
                <Input type="number" min="1" required value={it.qty} onChange={(e) => set(i, { qty: e.target.value })} className="h-9 text-sm" data-testid={`shipment-item-qty-${i}`} />
                <Input type="number" min="0" value={it.price} onChange={(e) => set(i, { price: e.target.value })} placeholder="Harga/unit" className="h-9 text-sm" data-testid={`shipment-item-price-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="h-9 w-8 text-rose-400" disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`shipment-item-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { ...EMPTY }])} data-testid="shipment-add-item"><Plus className="w-4 h-4 mr-1" /> Tambah Barang</Button>
          </div>
          <div className="space-y-1.5"><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="opsional (ekspedisi, resi, dll)" data-testid="shipment-note" /></div>
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="text-sm">Total nilai: <span className="font-bold font-mono" data-testid="shipment-total">{rupiah(total)}</span></div>
            <Button type="submit" disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="shipment-submit">{busy ? "Menyimpan..." : "Simpan (Disiapkan)"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
